import { cn } from '@rocco/utils/cn';
import type { CartItem } from '~/modules/ecommerce/type/cart-item';
import { DRAWER_CTA } from './drawer-cta';

/** Where the cart lives. A full load: the cart is its own page, not a route of this one. */
const CART_URL = '/shopping-cart';

interface DrawerCartPanelProps {
    /**
     * What the pane is: the cart, or the confirmation of an add. The product
     * drawer arrives here having just added something and says so; the header's
     * icon is only ever opening the cart.
     */
    title?: string;
    /**
     * The cart's lines and its total, from whichever surface knows them: the
     * product page's own summary, or the root loader's on every other page.
     */
    items: CartItem[];
    subtotal?: string | null;
    /** Shuts the drawer — the pane's own quiet action. */
    onClose: () => void;
    /**
     * The line the add just created. The cart summary is loader data and only
     * catches up on revalidation, so the confirmation leads with this and falls
     * back to the summary — a reader who has just pressed the button should
     * never be shown an empty cart.
     */
    justAdded?: CartItem | null;
}

/**
 * The cart's contents, as a drawer pane.
 *
 * It replaces the header's little menu: that popup opens at the top of the
 * window, several hundred pixels from whatever filled it, over a page the
 * reader is still using. Shown here, on the product drawer's second pane after
 * an add and on its own from the header's cart icon.
 *
 * Its lines are passed in rather than read here — the product page has its own
 * cart summary and every other page has the root loader's, and this pane should
 * not care which it is looking at.
 */
export const DrawerCartPanel = ({
    title = 'Your Cart',
    items: cartItems,
    subtotal,
    justAdded,
    onClose,
}: DrawerCartPanelProps) => {
    const items =
        cartItems.length > 0 ? cartItems : justAdded ? [justAdded] : [];

    return (
        <div className="flex h-full min-h-0 flex-col">
            <div className="flex min-h-0 flex-1 flex-col overflow-x-hidden overflow-y-auto overscroll-contain">
                <div className="px-4 pt-[4.5rem]">
                    <p className="m-0 font-sans text-xl leading-[1.2] font-medium tracking-[-0.02em] text-[#181818]">
                        {title}
                    </p>

                    <ul className="m-0 mt-5 flex list-none flex-col gap-[1rem] p-0">
                        {items.map((item) => (
                            <li
                                key={item.ID}
                                className="flex items-start gap-[0.75rem]"
                            >
                                {item.Variant?.Thumbnail?.Desktop ? (
                                    <img
                                        src={item.Variant.Thumbnail.Desktop}
                                        alt=""
                                        className="size-[3.5rem] shrink-0 rounded-[0.75rem] bg-[#f5f5f7] object-cover"
                                    />
                                ) : null}
                                <span className="min-w-0 flex-1">
                                    <span className="block font-sans text-[0.875rem] leading-[1.3] text-[#181818]">
                                        {item.Variant?.Product?.Title}
                                    </span>
                                    <span className="mt-[0.125rem] block font-sans text-[0.8125rem] leading-[1.3] text-[#9b9b9b]">
                                        {item.Price?.ShortFormat}
                                        {item.Sample ? ' · Sample' : null}
                                    </span>
                                </span>
                                <span className="shrink-0 font-sans text-[0.875rem] text-[#181818]">
                                    &times;{item.Quantity}
                                </span>
                            </li>
                        ))}
                    </ul>
                </div>
            </div>

            {/* The same pair the product side closes on: the total rides in
                the dark button, as the price rides in Add to Cart, with the
                quiet one under it. */}
            <div className="flex shrink-0 flex-col gap-3 border-t border-black/10 px-4 pt-4 pb-6">
                <a
                    href={CART_URL}
                    className={cn(DRAWER_CTA('filled-light'), 'no-underline')}
                >
                    {subtotal ? `${subtotal} — View Cart` : 'View Cart'}
                </a>
                <button
                    type="button"
                    onClick={onClose}
                    className={DRAWER_CTA('white-lifted')}
                >
                    Close
                </button>
            </div>
        </div>
    );
};
